/**
 * ClickReel SDK - Logger Utility
 *
 * Sprint 008 (2026-04-29): `error()` now also POSTs to a remote logger
 * endpoint when configured. Sprint 005 stripped `console.error` from the
 * production bundle via Terser `pure_funcs`, so dev consoles are quiet — but
 * we lost the field-debugging signal. The remote post path is INTENTIONALLY
 * NOT a console.* call so Terser leaves it in place.
 *
 * The post is fire-and-forget: keepalive: true, no retry, errors swallowed.
 * It must NOT block any caller (caller is typically already in an error
 * branch) and must NOT throw.
 */

interface ErrorReportingConfig {
  apiUrl: string;
  siteKey: string;
  enabled: boolean;
  sessionId?: string;
}

// Module-level, lazily-set by configureErrorReporting() once the SDK has the
// resolved siteKey + apiUrl from init. Until then, error reports are
// console-only (the existing pre-Sprint-008 behaviour).
let _errorReportingConfig: ErrorReportingConfig | null = null;

/**
 * Configure remote error reporting. Called from the SDK's main init path
 * once the siteKey + apiUrl are known.
 *
 * @param config.siteKey   Site UUID (forwarded to the server for tenancy)
 * @param config.apiUrl    Ingest base URL (e.g., https://api.clickreel.io)
 * @param config.enabled   Whether to actually POST. Allows host pages to
 *                         opt out via `errorReporting: { enabled: false }`.
 * @param config.sessionId Optional session ID for correlation (set after
 *                         /v1/sdk/init resolves a session).
 */
export function configureErrorReporting(config: {
  siteKey: string;
  apiUrl: string;
  enabled: boolean;
  sessionId?: string;
}): void {
  _errorReportingConfig = {
    siteKey: config.siteKey,
    apiUrl: config.apiUrl.replace(/\/$/, ''),
    enabled: config.enabled,
    sessionId: config.sessionId,
  };
}

/**
 * Test-only / SDK-internal accessor for the current remote config.
 */
export function _getErrorReportingConfig(): ErrorReportingConfig | null {
  return _errorReportingConfig;
}

/**
 * Test-only helper: clear remote-logger config between tests.
 */
export function _resetErrorReporting(): void {
  _errorReportingConfig = null;
}

/**
 * Send an error report to the remote logger endpoint, fire-and-forget.
 *
 * - Returns immediately; the caller never awaits.
 * - Uses fetch with `keepalive: true` so reports survive page-unload races.
 * - Catches all failures silently — we are already in an error branch and
 *   must not cascade. NO retry: drop on the floor if delivery fails.
 *
 * Crucially, this function does NOT call console.* — that's important
 * because Terser is configured to strip those in the production bundle.
 * If this code path went through console.error, the production bundle would
 * effectively become a no-op for remote reporting.
 */
function postRemoteError(messageParts: unknown[]): void {
  const cfg = _errorReportingConfig;
  if (!cfg || !cfg.enabled || !cfg.siteKey || !cfg.apiUrl) {
    return;
  }
  // Best-effort: only run in a browser-ish env that has fetch.
  if (typeof fetch !== 'function') return;

  let errorMessage: string;
  let errorContext: string | undefined;
  try {
    // First arg is the canonical message; remaining args (if any) become a
    // context blob via JSON-safe stringification. This mirrors how the SDK
    // calls logger.error('Initialization failed:', error) — the error
    // object lands in `errorContext`.
    errorMessage = messageParts.length > 0 ? safeStringify(messageParts[0]) : 'Unknown error';
    if (messageParts.length > 1) {
      errorContext = messageParts.slice(1).map(safeStringify).join(' ');
    }
  } catch {
    errorMessage = 'Unknown error (stringify failure)';
  }

  let pageUrl: string | undefined;
  try {
    pageUrl = typeof window !== 'undefined' ? window.location?.href : undefined;
  } catch {
    pageUrl = undefined;
  }

  let userAgent: string | undefined;
  try {
    userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : undefined;
  } catch {
    userAgent = undefined;
  }

  const body = JSON.stringify({
    siteKey: cfg.siteKey,
    sessionId: cfg.sessionId,
    errorMessage,
    errorContext,
    userAgent,
    url: pageUrl,
    timestamp: Date.now(),
  });

  try {
    // No await. Promise rejections silenced by .catch noop.
    fetch(`${cfg.apiUrl}/v1/sdk/errors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      // Avoid forcing CORS preflight unnecessarily — the server treats this
      // as a public SDK endpoint and accepts any origin.
      credentials: 'omit',
      mode: 'cors',
    }).catch(() => {
      /* fire-and-forget: drop on floor */
    });
  } catch {
    /* synchronous throw from fetch (e.g. blocked by CSP) — drop */
  }
}

/**
 * JSON-safe stringification for arbitrary log args. Errors get .message +
 * .name + .stack; objects get JSON.stringify with circular-reference
 * fallback.
 */
function safeStringify(value: unknown): string {
  if (value === null || value === undefined) return String(value);
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (value instanceof Error) {
    return `${value.name}: ${value.message}${value.stack ? '\n' + value.stack : ''}`;
  }
  try {
    return JSON.stringify(value);
  } catch {
    // Circular reference or other JSON error
    try {
      return String(value);
    } catch {
      return '[unstringifiable]';
    }
  }
}

export class Logger {
  private prefix = '[ClickReel]';
  private debugEnabled = false;

  setDebug(enabled: boolean): void {
    this.debugEnabled = enabled;
  }

  log(...args: unknown[]): void {
    console.log(this.prefix, ...args);
  }

  debug(...args: unknown[]): void {
    if (this.debugEnabled) {
      console.debug(this.prefix, '[DEBUG]', ...args);
    }
  }

  warn(...args: unknown[]): void {
    console.warn(this.prefix, ...args);
  }

  error(...args: unknown[]): void {
    // (a) Native console.error — stripped in production bundle (Sprint 005)
    //     but still useful in dev / non-minified builds.
    console.error(this.prefix, ...args);
    // (b) Remote post — survives Terser pure_funcs because it does NOT go
    //     through console.* and is gated on a configured siteKey + enabled
    //     flag. No-ops cleanly if the SDK never called configureErrorReporting().
    postRemoteError(args);
  }
}

export const logger = new Logger();
